// src/components/Procurement/StatusBadge.jsx
// Shared badge components for procurement status axes.
// DeliveryBadge — the M10 two-axis delivery PAIR { receipt, timing } (Prompt 121).
//   Takes a DERIVED pair (from src/lib/deliveryStatus.js), NOT a raw receipt_status.
//   The old internal today>ETA overlay + raw-status echo are DELETED (the caller now
//   owns derivation via deriveLineStatus/deriveOrderStatus).
// PaymentBadge  — finance status + overdue override
import React from 'react';
import { RECEIPT_LABELS } from '../../lib/deliveryStatus';
import i18n from '../../i18n';

import { emoText } from '../../shell/EmojiIcons';
const DELIVERY_LABELS = RECEIPT_LABELS;
// cancelled + partially_cancelled → neutral GREY (terminal-void), DISTINCT from received-green.
// partially_cancelled shares the same grey (some received, but the order is a terminal void).
// 710: the delivery axis carries EXACTLY its 3 ruled values. cancelled / partially_cancelled were
// ruled OFF this axis by 417 (they are DISPOSITION) and their keys are deleted here — the engine
// never emits them on delivery_status (708 verified 0 live), so nothing renderable is lost.
// 791: EXPORTED (was module-private). Reports ▸ Transactions ▸ Purchase renders the RAW stored
// purchase_detail.receipt_status and must speak these exact words/tones — reading them from here is
// what stops the report growing a second buy-side vocabulary that can drift out of step with the chip.
export const DELIVERY_COLORS = { en_route: '#2563eb', partial: '#c47d10', received: '#16a34a' };
export const DELIVERY_BG     = { en_route: '#eff6ff', partial: '#fff7ed', received: '#f0fdf4' };

const PAYMENT_LABELS  = { Paid: 'Paid', completed: 'Paid', Pending: 'Pending', Credit: 'Credit', Auto: 'Auto' };
const PAYMENT_COLORS  = { Paid: '#16a34a', completed: '#16a34a', Pending: '#c47d10', Credit: '#2563eb', Auto: '#6b7280' };
const PAYMENT_BG      = { Paid: '#f0fdf4', completed: '#f0fdf4', Pending: '#fef9ec', Credit: '#eff6ff', Auto: '#f3f4f6' };

// SF110-B2.3 cancellation-aware payment states (owner rulings SF110-B2.3-PAYMENT-AXIS-LABELS):
//  - 'settled'    : net AP == 0 because the order was cancelled/reversed (obligation closed —
//                   NOT 'Pending', NOT a bare dash). Quiet neutral slate (not red, not success-green).
//  - 'refund-due' : net AP < 0, supplier owes you (the B1 refund_receivable leg). First-class
//                   "money-owed-to-you" pill in a teal tone, distinct from red overdue (you-owe).
const PAY_STATE_STYLE = {
  settled:      { bg: '#f1f5f9', color: '#64748b', label: 'Settled' },     // slate-100 / slate-500
  'refund-due': { bg: '#f0fdfa', color: '#0d9488', label: 'Refund Due' },  // teal-50 / teal-600
};

// E12 money-clock — the payment axis is backend-owned. The renderer only maps the emitted state
// to a named shell status token; it never infers due dates, credits, or an advance from amounts.
// When the caller passes `p1`, it takes precedence over the legacy finance_status/payState path.
const P1_STYLE = {
  paid:              { bg: 'var(--st-good-bg)',    color: 'var(--st-good)',    label: 'Paid' },
  partial:           { bg: 'var(--st-warn-bg)',    color: 'var(--st-warn)',    label: 'Partial' },
  unpaid:            { bg: 'var(--st-crit-bg)',    color: 'var(--st-crit)',    label: 'Unpaid' },
  void:              { bg: 'var(--st-neutral-bg)', color: 'var(--st-neutral)', label: 'Void' },
  not_yet_due:       { bg: 'var(--st-neutral-bg)', color: 'var(--st-neutral)', label: 'Not yet due' },
  overdue:           { bg: 'var(--st-crit-bg)',    color: 'var(--st-crit)',    label: 'Overdue' },
  paid_in_advance:   { bg: 'var(--st-ahead-bg)',   color: 'var(--st-ahead)',   label: 'Paid in advance' },
  'prepaid-partial': { bg: 'var(--st-ahead-bg)',   color: 'var(--st-ahead)',   label: 'Prepaid (partial)' },
  overpaid:          { bg: 'var(--st-attn-bg)',    color: 'var(--st-attn)',    label: 'Overpaid' },
};

const pillStyle = (bg, color) => ({
  display: 'inline-block',
  background: bg || '#f3f4f6',
  color: color || '#6b7280',
  padding: '2px 9px',
  borderRadius: 9999,
  fontSize: 11,
  fontWeight: 700,
  whiteSpace: 'nowrap',
});

/**
 * DeliveryBadge — renders the M10 derived delivery PAIR (receipt + timing).
 * Dumb renderer: the caller derives the pair via deriveLineStatus/deriveOrderStatus
 * (src/lib/deliveryStatus.js) and passes it. Shows the receipt-axis pill, and appends
 * a red "· Late" chip when the timing axis is late (M10 'received late' / outstanding-late).
 * Props:
 *   pair:     { receipt: 'en_route'|'partial'|'received', timing: 'on_time'|'late' }
 *             710: callers pass the ENGINE's delivery_status / delivery_timing, not a local derivation.
 *   lateDays: per-order late-day count (engine delivery_late_days) → renders "Late Nd"
 *   size:     'sm' | 'md' (default 'md')
 *   showTiming: false renders PROGRESS ONLY — for surfaces that carry a separate Timing column, so
 *             the Late chip is not drawn twice. Timing still renders, just in its own column.
 */
export function DeliveryBadge({ pair, lateDays = null, size = 'md', showTiming = true }) {
  const receipt = pair?.receipt || 'en_route';
  const late = pair?.timing === 'late';
  const fs = 11;

  const progress = DELIVERY_LABELS[receipt]
    ? (
      <span style={{ ...pillStyle(DELIVERY_BG[receipt], DELIVERY_COLORS[receipt]), fontSize: fs }}>
        {DELIVERY_LABELS[receipt]}
      </span>
    )
    : null;

  if (!late || !showTiming) return progress;

  // 710: progress and timing are TWO chips side by side — timing never replaces progress.
  // The "Late Nd" day-count is folded in from OrdersTab's deleted DeliveryOrderBadge; ONE form
  // everywhere ("Late Nd" when a per-order day count exists, bare "Late" otherwise).
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      {progress}
      <span style={{ ...pillStyle('var(--red-bg)', 'var(--red)'), fontSize: fs, whiteSpace: 'nowrap' }}>
        {Number(lateDays) > 0 ? `Late ${Number(lateDays)}d` : 'Late'}
      </span>
    </span>
  );
}

/**
 * PaymentBadge — shows finance status pill, or red "Overdue" if past due.
 * Props:
 *   finance_status: string | null ('Paid' | 'Pending' | 'Credit' | 'Auto')
 *   finance_date: string | null (YYYY-MM-DD, invoice date)
 *   payment_days: number | null
 *   size: 'sm' | 'md' (default 'md')
 *   overdueDays: number | null — 791: an ENGINE-supplied past-due day count (E10 order payment aging /
 *     E12 v_sales_order.days_overdue). When > 0 an "Overdue" chip renders BESIDE the status pill,
 *     exactly as DeliveryBadge renders its "Late Nd" chip beside the progress pill — one overdue form
 *     on both Orders surfaces (procurement and sales), never two divergent local pills.
 *     Dumb renderer, the 710 discipline: the CALLER owns the "still owed AND past due" rule and passes
 *     null when it does not hold. This component never derives a due date or compares to today.
 *
 *     791 OWNER RULING (ORDER-LEVEL-OVERDUE-IS-BINARY): this is an ORDER-row badge, and an order can
 *     span several lines with DIFFERENT due dates — so a single day count on the order row would
 *     contradict the lines beneath it. The value is therefore the TRIGGER ONLY and is deliberately NOT
 *     rendered: the chip reads a bare "Overdue". The order-level fact is binary ("something here is
 *     overdue"), so the badge says exactly that and no more. A day count is unambiguous only at the
 *     LINE grain, which is where it belongs. The prop stays a number rather than a boolean because
 *     every caller already holds the count (payAgeDays / days_overdue) and a truthy>0 test is the same
 *     "past due" predicate — nothing is gained by making each caller pre-coerce it.
 *   advanceOverdueDays: number | null — E12's advance_days_overdue, rendered only with the E12
 *     `overdue` state. It is a display suffix supplied by the engine, never a local clock calculation.
 */
export function PaymentBadge({ finance_status, finance_date, payment_days, payState = null, p1 = null, overdueDays = null, advanceOverdueDays = null, size = 'md' }) {
  const fs0 = 11;
  // 791: the shared overdue chip. Same pill geometry and the same red tokens the legacy
  // "Overdue Nd" arm below already uses, so the two paths are visually identical.
  // Per the ORDER-LEVEL-OVERDUE-IS-BINARY ruling above, `overdueDays` is only the trigger — the count
  // is intentionally not interpolated, because on a multi-line order it would contradict the lines.
  // E12's explicit `overdue` state already owns its pill (and optional advance-days suffix).
  // Do not append the legacy aging pill as well, or an overdue sale would read twice.
  const overdueChip = p1 !== 'overdue' && Number(overdueDays) > 0
    ? <span style={{ ...pillStyle('var(--red-bg)', 'var(--red)'), fontSize: fs0, whiteSpace: 'nowrap' }}>{`Overdue ${Number(overdueDays)}d`}</span>
    : null;
  // Pair the status pill with the overdue chip. Either may be absent (a `void` order renders no status
  // pill; a current order renders no overdue chip) — return whichever exists rather than an empty wrapper.
  const withOverdue = (pill) => {
    if (!overdueChip) return pill;
    if (!pill) return overdueChip;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>{pill}{overdueChip}</span>
    );
  };
  // The engine's E12 payment_status takes precedence when present. When it is absent, fall through
  // to the legacy finance/payState path for procurement callers that have not moved to this axis.
  if (p1 != null && Object.prototype.hasOwnProperty.call(P1_STYLE, p1)) {
    const s = P1_STYLE[p1];
    const label = p1 === 'overdue' && Number(advanceOverdueDays) > 0
      ? `${s.label} (${Number(advanceOverdueDays)} days)`
      : s.label;
    return withOverdue(s ? <span style={{ ...pillStyle(s.bg, s.color), fontSize: fs0 }}>{emoText(label)}</span> : null);
  }
  // SF110-B2.3: a cancellation-aware state (settled / refund-due) overrides the legacy
  // finance_status axis. Passed by callers that compute the netted AP position.
  if (payState && PAY_STATE_STYLE[payState]) {
    const s = PAY_STATE_STYLE[payState];
    return <span style={{ ...pillStyle(s.bg, s.color), fontSize: fs0 }}>{emoText(s.label)}</span>;
  }
  if (!finance_status) return null;
  // Normalize legacy lowercase 'completed' → 'Paid'
  const normalizedStatus = finance_status === 'completed' ? 'Paid' : finance_status;
  const today = new Date().toISOString().slice(0, 10);
  const fs = 11;

  if (normalizedStatus === 'Pending' && finance_date != null && payment_days != null) {
    const dueDate = new Date(new Date(finance_date).getTime() + payment_days * 86400000).toISOString().slice(0, 10);
    if (dueDate < today) {
      const days = Math.round((new Date(today) - new Date(dueDate)) / 86400000);
      return (
        <span style={{ ...pillStyle('var(--red-bg)', 'var(--red)'), fontSize: fs }}>
          Overdue {days}d
        </span>
      );
    }
  }

  return (
    <span style={{ ...pillStyle(PAYMENT_BG[normalizedStatus], PAYMENT_COLORS[normalizedStatus]), fontSize: fs }}>
      {PAYMENT_LABELS[normalizedStatus] || normalizedStatus}
    </span>
  );
}

/**
 * DispositionBadge — the SET-VALUED disposition axis (9 ruled values).
 * 710: one shared renderer. The chip existed inline in three places (OrdersTab, OperationDetailView)
 * plus a 4th local map in DispositionSection that covered only 4 of the 9 values and mislabelled
 * `stranded`. All of them now render through here, over lcDisposition's orderDispositionBadge().
 * Props: disp — the object returned by orderDispositionBadge(), or null (null → no chip).
 */
export function DispositionBadge({ disp, size = 'md' }) {
  if (!disp) return null;
  const fs = 11;
  const tone = disp.tone === 'open'
    ? { bg: '#fff7ed', color: '#c47d10' }   // amber — actionable disposition
    : { bg: '#f0fdfa', color: '#0d9488' };  // teal — settled/resolved disposition
  const extra = Number(disp.extraCount) || 0;
  const folded = extra > 0 && disp.others?.length ? ` — also: ${disp.others.join(', ')}` : '';
  return (
    <span
      title={`Disposition axis — see the Disposition section on the order page${folded}`}
      style={{ ...pillStyle(tone.bg, tone.color), fontSize: fs }}
    >
      {emoText(disp.label)}{extra > 0 ? ` +${extra}` : ''}
    </span>
  );
}

/**
 * LifecycleBadge — the ORDER-LIFECYCLE axis (E12 `order_lifecycle`: quote | planned | open | closed).
 * 943 (item 15): the sales-orders table already FILTERED on this field but never displayed it — the
 * inverse of a filter-with-no-data gap: the user could narrow by a state the row would not show. Added
 * to the SHARED module rather than inlined, so it carries the same pill shape/sizing as its siblings
 * and there is one lifecycle vocabulary if another surface needs it.
 * `closed` is deliberately the QUIET tone: a closed order is the resting state, not an alert.
 */
export const ORDER_LIFECYCLE = {
  quote:   { label: 'Quote',   bg: '#f1f5f9', color: '#475569' },
  planned: { label: 'Planned', bg: '#eff6ff', color: '#2563eb' },
  open:    { label: 'Open',    bg: '#fff7ed', color: '#c47d10' },
  closed:  { label: 'Closed',  bg: '#f0fdf4', color: '#16a34a' },
};
export function LifecycleBadge({ status, size = 'md' }) {
  if (!status) return null;
  const t = ORDER_LIFECYCLE[status] || { label: String(status), bg: '#f1f5f9', color: '#475569' };
  return (
    <span title={`Order lifecycle — E12 order_lifecycle = ${status}`}
          style={{ ...pillStyle(t.bg, t.color), fontSize: 11 }}>
      {emoText(t.label)}
    </span>
  );
}

/**
 * TimingBadge — the TIMING axis on its own, for surfaces with a dedicated Timing column.
 * 710: replaces SuppliersTab's bespoke ✓/✗ under an "On time?" header. One vocabulary everywhere.
 */
export function TimingBadge({ timing, lateDays = null, size = 'md' }) {
  if (!timing) return null;
  const fs = 11;
  if (timing === 'late') {
    return (
      <span style={{ ...pillStyle('var(--red-bg)', 'var(--red)'), fontSize: fs, whiteSpace: 'nowrap' }}>
        {Number(lateDays) > 0 ? `Late ${Number(lateDays)}d` : 'Late'}
      </span>
    );
  }
  return <span style={{ ...pillStyle('#f0fdf4', '#16a34a'), fontSize: fs }}>On time</span>;
}

// ── 735 — SELL-SIDE (Sales ▸ Orders) ─────────────────────────────────────────────────────────
// The sell-side delivery axis lives HERE, beside the buy-side one, so there is exactly one badge
// module and one visual vocabulary. It is a SIBLING rather than extra keys on DELIVERY_LABELS on
// purpose: 417 deliberately ruled `cancelled` OFF the buy-side delivery axis (it is disposition
// there), and adding the key back to the shared map would quietly undo that ruling for procurement.
// On the SELL side `cancelled` IS a delivery_status value (it is in the CHECK), so the two axes have
// genuinely different value sets and each keeps its own.
//
// Colours mirror the buy-side tones so the two surfaces read identically:
//   preparing → blue (in flight)   partial → amber   delivered → green   quote/cancelled → grey
// 791: EXPORTED — this is THE sell-side delivery vocabulary. Reports ▸ Transactions ▸ Sales reads it
// for BOTH its filter <option> list and its Status column, so the report cannot say a different word
// (or offer a different value set) than Sales ▸ Orders. 789 §3 is exactly what happens otherwise: the
// filter and the column were two independent hardcoded readers of sales.delivery_status and drifted —
// the filter still offered `committed` (remapped away by db/456_720, 0 live rows) while `preparing`,
// the value carrying the entire 13,800 reconciliation gap, had no option at all.
// 833 — 'uncommitted' joins the axis. db/483 added it to sales_delivery_status_check (mirroring
// db/466:222, which already widened the same CHECK once to admit 'quote') and stamped every PLANNED
// sale with it. Owner ruling 2026-08-16: "Planned are not deferred and not preparing. They are
// nothing. Only posted can be deferred or preparing or delivered or paid." Before this, the NOT NULL
// column forced POST /api/sales to stamp a plan from delivery_mode alone — 'preparing' (stock
// reserved) for deferred, 'delivered' (the goods have gone) for direct. Neither was true.
// GREY, reusing `cancelled`'s existing chip tone rather than introducing a colour: grey is already
// this vocabulary's "not on the progress track" tone, and a plan claims no progress. No new scheme.
export const SALE_DELIVERY = {
  quote:       { label: 'Quote',       bg: '#f5f3ff', color: '#7c3aed' },
  uncommitted: { label: 'Uncommitted', bg: '#f3f4f6', color: '#6b7280' },
  preparing:   { label: 'Preparing',   bg: '#eff6ff', color: '#2563eb' },
  partial:     { label: 'Partial',     bg: '#fff7ed', color: '#c47d10' },
  delivered:   { label: 'Delivered',   bg: '#f0fdf4', color: '#16a34a' },
  cancelled:   { label: 'Cancelled',   bg: '#f3f4f6', color: '#6b7280' },
};

/**
 * SaleDeliveryBadge — the sell-side delivery axis, read STRAIGHT off E12's delivery_status.
 * Never derived in the FE: whatever the engine says is what renders (and an unknown value renders
 * as itself rather than disappearing, so a new axis value is visible instead of silently blank —
 * the 732 ELSE lesson, applied to the UI).
 * Props: status (E12 delivery_status) · timing (E12 delivery_timing) · size · showTiming
 */
export function SaleDeliveryBadge({ status, timing = null, size = 'md', showTiming = true }) {
  if (!status) return null;
  const fs = 11;
  const s = SALE_DELIVERY[status];
  const progress = (
    <span style={{ ...pillStyle(s ? s.bg : '#f3f4f6', s ? s.color : '#6b7280'), fontSize: fs }}>
      {emoText(s ? s.label : status)}
    </span>
  );
  if (timing !== 'late' || !showTiming) return progress;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      {progress}
      <span style={{ ...pillStyle('var(--red-bg)', 'var(--red)'), fontSize: fs, whiteSpace: 'nowrap' }}>Late</span>
    </span>
  );
}

// E12's disposition_status is a SINGLE text value; the shared DispositionBadge takes the richer
// object orderDispositionBadge() builds on the buy side. This adapts one to the other so the sell
// side renders through the SAME component instead of growing a fourth local chip.
const SALE_DISPOSITION = {
  cancelled:           { label: 'Cancelled',           tone: 'resolved' },
  partially_cancelled: { label: 'Partially cancelled', tone: 'resolved' },
  written_off:         { label: 'Written off',         tone: 'resolved' },
  refunded_to_bank:    { label: 'Refunded to bank',    tone: 'resolved' },
  refund_available:    { label: 'Refund collectable',  tone: 'open' },
  refund_due:          { label: 'Refund due',          tone: 'open' },
};
const REFUND_VALUES = ['refund_available', 'refund_due', 'refunded_to_bank'];
export const SALE_DISPOSITION_VALUES = Object.keys(SALE_DISPOSITION);
export function saleDispositionBadge(value) {
  if (!value || REFUND_VALUES.includes(value)) return null;   // a clean order shows no chip; refund situations are the ONE RefundStateBadge (flags), never a second badge
  const d = SALE_DISPOSITION[value];
  return { kind: value, label: saleDispositionLabel(value), tone: d ? d.tone : 'open', extraCount: 0, others: [] };
}
export function saleDispositionLabel(value) {
  // EN text above is the fallback; the sales namespace (so.f.disp.*) carries EN + FR (owner wording: refund_available = 'Refund collectable' / 'Remboursement à récupérer')
  return value ? i18n.t(`so.f.disp.${value}`, { ns: 'sales', defaultValue: SALE_DISPOSITION[value]?.label || value }) : 'None';
}
